/**
 * Social / sharing schemas.
 *
 * A *shared dashboard* is a named, independently-editable copy of a
 * `DashboardConfig` that a user publishes under their public namespace
 * (`<public-base>/u/<username>/<slug>`). Each share is either **public**
 * (listed on the user's public profile) or **unlisted** (reachable only via the
 * unguessable slug).
 *
 * The federation key is a user's full public base URL, not `user@host`, so an
 * instance may be served over http or under a sub-path of another site. Public
 * responses therefore carry absolute `profile_url` / `share_url` strings rather
 * than a bare host.
 */
import { z } from 'zod';
import { challengeSpecSchema } from "./challenges.js";
import { chartDataBreakdownBucketSchema, chartDataBucketSchema } from "./chart-data.js";
import { baseResponseSchema } from "./common.js";
import { dashboardConfigSchema } from "./dashboard.js";
import { goalProgressSchema } from "./goals.js";
import { trendHistoryPointSchema } from "./trends.js";
import { shareVisibilitySchema } from "./visibility.js";
// =============================================================================
// Shared dashboard (owner-facing CRUD)
// =============================================================================
/** Display name for a shared dashboard. */
export const sharedDashboardNameSchema = z
    .string()
    .min(1)
    .max(120)
    .meta({ description: 'Display name for the shared dashboard' });
/**
 * A shared dashboard as seen by its owner (includes the editable config and the
 * absolute share URL).
 */
export const sharedDashboardSchema = z
    .object({
    config: dashboardConfigSchema.meta({ description: 'The dashboard configuration' }),
    created_at: z.string().meta({ description: 'Creation timestamp (ISO 8601)' }),
    id: z.string().uuid().meta({ description: 'Shared dashboard ID' }),
    name: sharedDashboardNameSchema,
    share_url: z.string().meta({ description: 'Absolute URL of the shared dashboard' }),
    slug: z.string().meta({ description: 'URL-safe public slug' }),
    updated_at: z.string().meta({ description: 'Last update timestamp (ISO 8601)' }),
    visibility: shareVisibilitySchema.meta({
        description: 'public → listed on the public profile; unlisted → reachable only by its link',
    }),
})
    .meta({ id: 'SharedDashboard' });
/** Body for creating a shared dashboard. */
export const createSharedDashboardBodySchema = z
    .object({
    config: dashboardConfigSchema.meta({ description: 'The dashboard configuration to publish' }),
    name: sharedDashboardNameSchema,
    visibility: shareVisibilitySchema
        .default('unlisted')
        .meta({ description: 'public → listed on the public profile; unlisted (default) → link-only' }),
})
    .meta({ id: 'CreateSharedDashboardBody' });
/** Body for updating a shared dashboard (all fields optional). */
export const updateSharedDashboardBodySchema = z
    .object({
    config: dashboardConfigSchema.optional().meta({ description: 'Replacement dashboard configuration' }),
    name: sharedDashboardNameSchema.optional(),
    visibility: shareVisibilitySchema
        .optional()
        .meta({ description: 'public → listed on the public profile; unlisted → link-only' }),
})
    .meta({ id: 'UpdateSharedDashboardBody' });
/** Response wrapping a single shared dashboard. */
export const sharedDashboardResponseSchema = baseResponseSchema
    .extend({ dashboard: sharedDashboardSchema.optional() })
    .meta({ id: 'SharedDashboardResponse' });
/** Response wrapping the owner's list of shared dashboards. */
export const sharedDashboardsResponseSchema = baseResponseSchema
    .extend({ dashboards: z.array(sharedDashboardSchema) })
    .meta({ id: 'SharedDashboardsResponse' });
// =============================================================================
// Per-widget data payloads (public viewer — minimal projection)
// =============================================================================
/** A single point of a metric time series. */
export const metricSeriesPointSchema = z
    .object({
    time: z.string().meta({ description: 'Timestamp (ISO 8601)' }),
    value: z.number().meta({ description: 'Metric value' }),
})
    .meta({ id: 'MetricSeriesPoint' });
export const metricCardDataSchema = z
    .object({
    count: z.number().nullable().meta({ description: 'Number of contributing days, if applicable' }),
    max: z.number().nullable().meta({ description: 'Period max, if applicable' }),
    trend_percent: z.number().nullable().meta({ description: 'Trend vs previous period (percent)' }),
    value: z.number().nullable().meta({ description: 'Headline value' }),
})
    .meta({ id: 'MetricCardData' });
export const sparklineCardDataSchema = z
    .object({
    count: z.number().nullable().meta({ description: 'Number of contributing days, if applicable' }),
    series: z.array(metricSeriesPointSchema).meta({ description: 'Sparkline time series' }),
    trend_percent: z.number().nullable().meta({ description: 'Trend vs previous period (percent)' }),
    value: z.number().nullable().meta({ description: 'Headline value' }),
})
    .meta({ id: 'SparklineCardData' });
export const trendChartDataSchema = z
    .object({
    breakdown_histories: z
        .record(z.string(), z.array(trendHistoryPointSchema))
        .optional()
        .meta({ description: 'Per-series trend histories keyed by series name, when broken down' }),
    breakdown_series: z
        .array(z.string())
        .optional()
        .meta({ description: 'Distinct series names when broken down' }),
    current_value: z.number().meta({ description: 'Current trend value' }),
    history: z.array(trendHistoryPointSchema).meta({ description: 'Historical trend values' }),
})
    .meta({ id: 'TrendChartData' });
export const barChartDataSchema = z
    .object({
    breakdown_buckets: z
        .array(chartDataBreakdownBucketSchema)
        .optional()
        .meta({ description: 'Bucketed values per series, when broken down' }),
    breakdown_series: z
        .array(z.string())
        .optional()
        .meta({ description: 'Distinct series names when broken down' }),
    buckets: z.array(chartDataBucketSchema).meta({ description: 'Bucketed chart values' }),
})
    .meta({ id: 'BarChartData' });
export const correlationDataSchema = z
    .object({
    hrv_after30: z.number().nullable().meta({ description: 'Mean HRV 30 min after the activity' }),
    hrv_before30: z.number().nullable().meta({ description: 'Mean HRV 30 min before the activity' }),
    hrv_during: z.number().nullable().meta({ description: 'Mean HRV during the activity' }),
    occurrences: z.number().meta({ description: 'Number of occurrences analyzed' }),
})
    .meta({ id: 'CorrelationData' });
/**
 * Aggregated activity stats — exactly the figures the ActivitySummary widget
 * renders. Each category is null when its visibility toggle is off, so a public
 * viewer never receives data for a hidden category, nor the raw activity list.
 */
export const activitySummaryExerciseSchema = z
    .object({
    count: z.number().meta({ description: 'Number of workout sessions' }),
    total_minutes: z.number().meta({ description: 'Total workout minutes' }),
})
    .meta({ id: 'ActivitySummaryExercise' });
export const activitySummarySleepSchema = z
    .object({
    avg_hours: z.number().nullable().meta({ description: 'Average sleep hours per night' }),
    count: z.number().meta({ description: 'Number of nights tracked' }),
})
    .meta({ id: 'ActivitySummarySleep' });
export const activitySummaryMeditationSchema = z
    .object({
    count: z.number().meta({ description: 'Number of meditation sessions' }),
    total_minutes: z.number().meta({ description: 'Total meditation minutes' }),
})
    .meta({ id: 'ActivitySummaryMeditation' });
export const activitySummaryDataSchema = z
    .object({
    exercise: activitySummaryExerciseSchema
        .nullable()
        .meta({ description: 'Workout aggregates, or null when hidden' }),
    meditation: activitySummaryMeditationSchema
        .nullable()
        .meta({ description: 'Meditation aggregates, or null when hidden' }),
    sleep: activitySummarySleepSchema
        .nullable()
        .meta({ description: 'Sleep aggregates, or null when hidden' }),
})
    .meta({ id: 'ActivitySummaryData' });
export const hrZoneDatumSchema = z
    .object({
    avg_seconds: z.number().nullable().meta({ description: 'Average seconds per day in this zone' }),
    metric: z.string().meta({ description: 'HR zone metric name (e.g. hr_zone_2_sec)' }),
})
    .meta({ id: 'HrZoneDatum' });
export const hrZonesDataSchema = z
    .object({
    hr_zone_start: z
        .array(z.number())
        .nullable()
        .meta({ description: 'Effective HR zone start thresholds (bpm)' }),
    zones: z.array(hrZoneDatumSchema).meta({ description: 'Per-zone average time' }),
})
    .meta({ id: 'HrZonesData' });
export const goalProgressDataSchema = z
    .object({
    goals: z.array(goalProgressSchema).meta({ description: 'Goal progress entries' }),
})
    .meta({ id: 'GoalProgressData' });
/**
 * Resolved data for one widget, discriminated by widget type. Built server-side
 * from the stored widget config only — never from viewer-supplied params. A
 * `null` data field means the widget renders without data (e.g. quick links, or
 * a widget whose data could not be resolved).
 */
export const widgetDataSchema = z
    .discriminatedUnion('type', [
    z.object({ data: metricCardDataSchema.nullable(), type: z.literal('metric_card') }),
    z.object({ data: sparklineCardDataSchema.nullable(), type: z.literal('sparkline_card') }),
    z.object({ data: trendChartDataSchema.nullable(), type: z.literal('trend_chart') }),
    z.object({ data: barChartDataSchema.nullable(), type: z.literal('bar_chart') }),
    z.object({ data: correlationDataSchema.nullable(), type: z.literal('correlation') }),
    z.object({ data: activitySummaryDataSchema.nullable(), type: z.literal('activity_summary') }),
    z.object({ data: z.null(), type: z.literal('quick_link') }),
    z.object({ data: hrZonesDataSchema.nullable(), type: z.literal('hr_zones') }),
    z.object({ data: goalProgressDataSchema.nullable(), type: z.literal('goal_progress') }),
])
    .meta({ id: 'WidgetData' });
/** Map of widget id → resolved widget data. */
export const widgetDataMapSchema = z.record(z.string(), widgetDataSchema).meta({ id: 'WidgetDataMap' });
// =============================================================================
// Public profile + public dashboard (unauthenticated viewer)
// =============================================================================
/** A public dashboard as listed on a user's public profile. */
export const publicDashboardListItemSchema = z
    .object({
    name: sharedDashboardNameSchema,
    share_url: z.string().meta({ description: 'Absolute URL of the shared dashboard' }),
    slug: z.string().meta({ description: 'URL-safe public slug' }),
})
    .meta({ id: 'PublicDashboardListItem' });
/**
 * A public challenge as listed on a user's public profile. The window and spec
 * let a reader (the challenge discovery of a following instance, a profile page)
 * tell an ongoing challenge from an ended one without fetching each; they are
 * optional only because instances from before 2026-09 list name + link alone.
 */
export const publicChallengeListItemSchema = publicDashboardListItemSchema
    .extend({
    end_ts: z.string().optional().meta({ description: 'End instant, exclusive (ISO 8601)' }),
    spec: challengeSpecSchema.optional(),
    start_ts: z.string().optional().meta({ description: 'Start instant, inclusive (ISO 8601)' }),
    timezone: z.string().optional().meta({ description: 'IANA timezone the date range was chosen in' }),
})
    .meta({ id: 'PublicChallengeListItem' });
/**
 * Response for a user's public profile page. Payload fields are optional so
 * error/not-found responses (which carry only `success`/`error`) type-check.
 */
export const publicProfileResponseSchema = baseResponseSchema
    .extend({
    challenges: z
        .array(publicChallengeListItemSchema)
        .optional()
        .meta({ description: 'Public challenges hosted by this user' }),
    dashboards: z
        .array(publicDashboardListItemSchema)
        .optional()
        .meta({ description: 'Public shared dashboards' }),
    profile_url: z.string().optional().meta({ description: 'Absolute URL of the public profile' }),
    username: z.string().optional().meta({ description: 'The profile username' }),
})
    .meta({ id: 'PublicProfileResponse' });
/**
 * Response for viewing a single public/unlisted shared dashboard. Payload
 * fields are optional so error/not-found responses type-check.
 */
export const publicSharedDashboardResponseSchema = baseResponseSchema
    .extend({
    config: dashboardConfigSchema.optional().meta({ description: 'Sanitized dashboard configuration' }),
    name: sharedDashboardNameSchema.optional(),
    profile_url: z.string().optional().meta({ description: 'Absolute URL of the owner public profile' }),
    share_url: z.string().optional().meta({ description: 'Absolute URL of this shared dashboard' }),
    type: z.literal('dashboard').optional().meta({ description: 'Resource type discriminator' }),
    widget_data: widgetDataMapSchema.optional().meta({ description: 'Resolved data per widget id' }),
})
    .meta({ id: 'PublicSharedDashboardResponse' });
//# sourceMappingURL=social.js.map